import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type {
  ReleaseDetail,
  ReleaseTestStatus,
  ReleaseTestSummary,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";
import { TEST_STATUS_LABELS } from "./releaseVocabulary";

/**
 * The QA lead's decision on the release as a whole (§27).
 *
 * <p>Signing off clean while QA-required tickets have failed is refused by the server, and this
 * says so before the attempt. The way through is a gate override — a named person accepting a
 * named risk, visible on the release forever. Letting the sign-off quietly swallow the failures
 * would put the same decision on the record as though nothing had been wrong.
 */
export function QaSignOffDrawer({
  release,
  summary,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  summary: ReleaseTestSummary;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [decision, setDecision] = useState<ReleaseTestStatus>("PASSED");
  const [comments, setComments] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (open) {
      setDecision(summary.qaStatus === "NOT_STARTED" ? "PASSED" : summary.qaStatus);
      setComments(summary.comments ?? "");
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hasFailures = summary.tally.failed > 0;
  const blockedByFailures = hasFailures && decision === "PASSED";

  const submit = () =>
    void run(
      () =>
        api.post(`/api/v1/releases/${release.id}/qa/sign-off`, {
          decision,
          comments: comments.trim() === "" ? null : comments.trim(),
          lockVersion: summary.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="QA sign-off"
      subtitle={`${release.releaseKey} — ${release.releaseName}`}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={blockedByFailures} loading={saving}>
            {saving ? "Recording…" : "Record decision"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <div className="rounded-lg bg-ink-50 px-3 py-2 text-sm">
          <p className="text-ink-700">
            {summary.tally.passed} of {summary.tally.required} tickets passed
            {summary.tally.failed > 0 && `, ${summary.tally.failed} failed`}
            {summary.tally.notTested > 0 && `, ${summary.tally.notTested} not tested`}.
          </p>
        </div>

        {blockedByFailures && (
          <InlineAlert tone="warning">
            {summary.tally.failed} QA-required{" "}
            {summary.tally.failed === 1 ? "ticket has" : "tickets have"} failed, so this cannot be
            signed off clean. Either put the failures right, or sign off with a waiver and accept
            the risk on the record.
          </InlineAlert>
        )}

        <Field label="Decision" required>
          <Select
            value={decision}
            onChange={(event) => setDecision(event.target.value as ReleaseTestStatus)}
          >
            {(["PASSED", "PASSED_WITH_WAIVER", "FAILED", "IN_PROGRESS"] as ReleaseTestStatus[])
              .map((value) => (
                <option key={value} value={value}>
                  {TEST_STATUS_LABELS[value]}
                </option>
              ))}
          </Select>
        </Field>

        <Field label="Comments" hint="Kept with the release, and read at the review.">
          <Textarea
            rows={4}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
          />
        </Field>
      </div>
    </Drawer>
  );
}
